"use client";

import { ChevronDown } from "lucide-react";
import {
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { Button } from "../../elements/button/button";
import { cn } from "../../lib/utils";
import { Column } from "../grid/column";
import { Container } from "../grid/container";
import { Row } from "../grid/row";
import { mainNavigationClasses } from "./main-navigation.constants";
import type {
  MainNavigationAction,
  MainNavigationItem,
  MainNavigationLink,
  MainNavigationTeaser,
} from "./main-navigation.types";
import { MegaMenu } from "./mega-menu/mega-menu";
import {
  MobileNavigation,
  type MobileNavigationProps,
} from "./mobile-navigation/mobile-navigation";
import { NavigationActions } from "./navigation-actions/navigation-actions";
import { getNavigationTrackingAttributes } from "./navigation-tracking";

export interface MainNavigationProps {
  items: MainNavigationItem[];
  /** Brand logo, usually wrapped in a link to the homepage. */
  logo: ReactNode;
  /** Header actions such as search, wishlist, cart and account. */
  actions?: MainNavigationAction[];
  /**
   * Action whose panel is open, `null` for none. Pass it together with
   * `onOpenActionIdChange` to open a panel from outside, e.g. the cart after a
   * product was added. Leave it undefined to let the header manage it.
   */
  openActionId?: string | null;
  onOpenActionIdChange?: (actionId: string | null) => void;
  /** Target of the skip link, e.g. `#main-content`. No skip link is rendered without it. */
  skipToContentHref?: string;
  /** Mega menu that is open on first render. */
  defaultOpenItemId?: string;
  /** Service links of the navigation itself, shown on the first mobile level. */
  serviceLinks?: MainNavigationLink[];
  /** Teaser of the navigation itself, shown on the first mobile level. */
  teaser?: MainNavigationTeaser;
  /**
   * Open state of the mobile menu. Pass it together with
   * `onMobileMenuOpenChange` to drive the menu from the mobile navigation bar,
   * which the app renders outside this component.
   */
  mobileMenuOpen?: boolean;
  onMobileMenuOpenChange?: (open: boolean) => void;
  /** Must match the `menuId` of the mobile navigation bar. */
  mobileMenuId?: string;
  microcopies: MobileNavigationProps["microcopies"] & {
    skipToContent: string;
    /** Prefix of the submenu button label, e.g. "Show submenu: Biking". */
    submenu: string;
  };
}

const ITEM_ATTRIBUTE = "data-main-navigation-item";
const TOP_LEVEL_SELECTOR = "[data-main-navigation-top-level]";
const TOGGLE_SELECTOR = "[data-main-navigation-toggle]";
const PANEL_SELECTOR = "[data-main-navigation-panel]";
const PANEL_FOCUSABLE_SELECTOR = "a[href], button:not([disabled])";

/**
 * Site header with the level 1 navigation. On desktop, items with a mega menu
 * open it on hover, on click of their submenu button and with the arrow keys.
 * A hover-opened mega menu stays open while the pointer is on the level 1
 * items, the gap below them or the mega menu, and closes anywhere else. Header
 * actions with a panel open it on click; only one mega menu or panel is open
 * at a time. Below the desktop breakpoint the navigation moves into a drawer.
 */
export function MainNavigation(props: Readonly<MainNavigationProps>) {
  const [openItemId, setOpenItemId] = useState(props.defaultOpenItemId);
  const [ownOpenActionId, setOwnOpenActionId] = useState<string>();
  const [ownMobileMenuOpen, setOwnMobileMenuOpen] = useState(false);
  const idPrefix = useId();
  const headerRef = useRef<HTMLElement>(null);
  const pointerTypeRef = useRef("mouse");
  const isMobileMenuOpen = props.mobileMenuOpen ?? ownMobileMenuOpen;
  const openActionId =
    props.openActionId === undefined
      ? ownOpenActionId
      : (props.openActionId ?? undefined);

  function closeMobileMenu() {
    setOwnMobileMenuOpen(false);
    props.onMobileMenuOpenChange?.(false);
  }

  function setOpenActionId(actionId: string | undefined) {
    setOwnOpenActionId(actionId);
    props.onOpenActionIdChange?.(actionId ?? null);
  }

  // Mega menus and action panels share the space below the bar.
  function showMegaMenu(itemId: string | undefined) {
    setOpenItemId(itemId);
    if (itemId && openActionId) {
      setOpenActionId(undefined);
    }
  }

  function toggleActionPanel(actionId: string) {
    setOpenItemId(undefined);
    setOpenActionId(actionId === openActionId ? undefined : actionId);
  }

  function closeActionPanel() {
    setOpenActionId(undefined);
  }

  // Hover-opened menus must be dismissible without moving the pointer (WCAG 1.4.13).
  useEffect(() => {
    if (!openItemId) {
      return;
    }

    const itemId = openItemId;
    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key !== "Escape") {
        return;
      }
      const header = headerRef.current;
      if (header?.contains(document.activeElement)) {
        findItemElement(header, TOGGLE_SELECTOR, itemId)?.focus();
      }
      setOpenItemId(undefined);
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [openItemId]);

  function closeMenu() {
    setOpenItemId(undefined);
  }

  function closeAll() {
    closeMenu();
    if (openActionId) {
      closeActionPanel();
    }
  }

  // An open panel may hold typed input, so passing over the items keeps it.
  function handleItemPointerEnter(
    event: PointerEvent<HTMLLIElement>,
    item: MainNavigationItem
  ) {
    if (event.pointerType === "mouse" && !openActionId) {
      setOpenItemId(item.megaMenu ? item.id : undefined);
    }
  }

  function handleMousePointerLeave(event: PointerEvent<HTMLElement>) {
    if (event.pointerType === "mouse") {
      closeMenu();
    }
  }

  // Keyboard clicks have no detail and keep the last pointer type out of the decision.
  function getClickPointerType(event: MouseEvent<HTMLElement>) {
    const pointerType = event.detail > 0 ? pointerTypeRef.current : "keyboard";
    pointerTypeRef.current = "mouse";
    return pointerType;
  }

  function handleTopLevelLinkClick(
    event: MouseEvent<HTMLElement>,
    item: MainNavigationItem
  ) {
    const pointerType = getClickPointerType(event);

    // Touch devices cannot hover: the first tap opens the mega menu, the second one follows the link.
    if (
      item.megaMenu &&
      openItemId !== item.id &&
      pointerType !== "mouse" &&
      pointerType !== "keyboard"
    ) {
      event.preventDefault();
      showMegaMenu(item.id);
      return;
    }
    closeAll();
  }

  function handleToggleClick(
    event: MouseEvent<HTMLElement>,
    item: MainNavigationItem
  ) {
    const isOpen = openItemId === item.id;
    // A mouse click follows the hover that already opened the menu, so it must not close it again.
    const shouldClose = isOpen && getClickPointerType(event) !== "mouse";
    showMegaMenu(shouldClose ? undefined : item.id);
  }

  function handleBlur(event: FocusEvent<HTMLElement>) {
    const nextFocus = event.relatedTarget;
    if (nextFocus instanceof Node && !event.currentTarget.contains(nextFocus)) {
      closeAll();
    }
  }

  function handleNavKeyDown(event: KeyboardEvent<HTMLElement>) {
    const target = event.target as HTMLElement;
    const panel = target.closest<HTMLElement>(PANEL_SELECTOR);

    if (panel) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        moveFocus(
          Array.from(
            panel.querySelectorAll<HTMLElement>(PANEL_FOCUSABLE_SELECTOR)
          ),
          target,
          event.key
        );
      }
      return;
    }

    const itemId = target.getAttribute(ITEM_ATTRIBUTE);
    if (!itemId) {
      return;
    }

    if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      const topLevelControls = Array.from(
        event.currentTarget.querySelectorAll<HTMLElement>(TOP_LEVEL_SELECTOR)
      );
      const current = topLevelControls.find(
        (control) => control.getAttribute(ITEM_ATTRIBUTE) === itemId
      );
      if (current) {
        moveFocus(topLevelControls, current, event.key, true);
      }
      return;
    }

    const item = props.items.find((candidate) => candidate.id === itemId);
    if (event.key === "ArrowDown" && item?.megaMenu) {
      event.preventDefault();
      flushSync(() => showMegaMenu(item.id));
      const panelId = getPanelId(idPrefix, item.id);
      document
        .getElementById(panelId)
        ?.querySelector<HTMLElement>(PANEL_FOCUSABLE_SELECTOR)
        ?.focus();
    }
  }

  const openItem = props.items.find((item) => item.id === openItemId);
  const hasOpenActionPanel = Boolean(
    openActionId &&
      props.actions?.some(
        (action) => action.id === openActionId && action.panel
      )
  );

  return (
    <header
      // box-content: the bar itself is at most 1420px, with the gaps around it.
      className="component-main-navigation relative z-40 mx-auto box-content max-w-[1420px] px-10 text-black lg:pt-8"
      onBlur={handleBlur}
      ref={headerRef}
    >
      {openItem?.megaMenu || hasOpenActionPanel ? (
        // Dims the page, including the gaps around the floating bar.
        <div
          aria-hidden="true"
          className="fixed inset-0 hidden bg-black/40 opacity-100 starting:opacity-0 transition-opacity duration-200 ease-out motion-reduce:transition-none lg:block"
          data-testid="main-navigation-backdrop"
          onClick={closeAll}
          onPointerEnter={handleMousePointerLeave}
        />
      ) : null}

      {props.skipToContentHref ? (
        <Button
          className="pointer-events-none absolute top-2 left-4 z-50 opacity-0 focus-visible:pointer-events-auto focus-visible:opacity-100"
          href={props.skipToContentHref}
          mode="D2C"
          size="sm"
          variant="outline"
        >
          {props.microcopies.skipToContent}
        </Button>
      ) : null}

      {/* Below the desktop breakpoint the header is the mobile navigation bar,
          which the app renders outside this component. */}
      <Container
        className="hidden rounded-lg bg-white shadow-[0_0_10px_0_rgba(0,0,0,0.15)] lg:block"
        fullWidth={true}
        outerContainerOnly={true}
        paddingBottom="none"
        paddingTop="none"
        testId="main-navigation-bar"
      >
        {/* Not positioned, so that the mega menus are positioned against the whole bar. */}
        <div className="container--fluid">
          <Row className="static h-12 items-center">
            <Column className="col-span-2 flex items-center gap-2">
              {props.logo}
            </Column>

            {/* The navigation fills the bar's height, so that the pointer stays
                inside it on its way from an item down to the mega menu. Leaving
                it anywhere else closes a hover-opened menu. */}
            <Column className="col-span-8 flex justify-center self-stretch">
              <nav
                aria-label={props.microcopies.navigationLabel}
                className="flex"
                onKeyDown={handleNavKeyDown}
                onPointerDown={(event) => {
                  pointerTypeRef.current = event.pointerType;
                }}
                onPointerLeave={handleMousePointerLeave}
              >
                <ul className="flex items-stretch gap-4 xl:gap-10">
                  {props.items.map((item) => {
                    const isOpen = item.id === openItemId;
                    const panelId = getPanelId(idPrefix, item.id);
                    const topLevelClassName = cn(
                      mainNavigationClasses.topLevel,
                      "whitespace-nowrap",
                      isOpen && mainNavigationClasses.topLevelActive
                    );

                    return (
                      <li
                        className="flex items-center"
                        key={item.id}
                        onPointerEnter={(event) =>
                          handleItemPointerEnter(event, item)
                        }
                      >
                        {/* Positions the submenu button without moving the other items. */}
                        <div className="relative flex items-center">
                          {item.href ? (
                            <Button
                              className={topLevelClassName}
                              data-main-navigation-item={item.id}
                              data-main-navigation-top-level=""
                              {...getNavigationTrackingAttributes(item, 1)}
                              href={item.href}
                              mode="D2C"
                              onClick={(event: MouseEvent<HTMLElement>) =>
                                handleTopLevelLinkClick(event, item)
                              }
                              target={item.target}
                              unstyled={true}
                            >
                              {item.label}
                            </Button>
                          ) : null}

                          {item.megaMenu ? (
                            // Items with a link get a submenu button that is only visible on keyboard focus.
                            <Button
                              aria-controls={panelId}
                              aria-expanded={isOpen}
                              aria-label={
                                item.href
                                  ? `${props.microcopies.submenu}: ${item.label}`
                                  : undefined
                              }
                              className={
                                item.href
                                  ? cn(
                                      mainNavigationClasses.focusRing,
                                      "-translate-y-1/2 pointer-events-none absolute top-1/2 left-full ml-1 opacity-0 focus-visible:pointer-events-auto focus-visible:opacity-100 focus-visible:outline-offset-0"
                                    )
                                  : topLevelClassName
                              }
                              data-main-navigation-item={item.id}
                              data-main-navigation-toggle={item.id}
                              data-main-navigation-top-level={
                                item.href ? undefined : ""
                              }
                              {...(item.href
                                ? {}
                                : getNavigationTrackingAttributes(item, 1))}
                              mode="D2C"
                              onClick={(event: MouseEvent<HTMLElement>) =>
                                handleToggleClick(event, item)
                              }
                              type="button"
                              unstyled={true}
                            >
                              {item.href ? (
                                <ChevronDown aria-hidden="true" />
                              ) : (
                                item.label
                              )}
                            </Button>
                          ) : null}

                          {item.href || item.megaMenu ? null : (
                            <span className={topLevelClassName}>
                              {item.label}
                            </span>
                          )}
                        </div>

                        {item.megaMenu ? (
                          <MegaMenu
                            hidden={!isOpen}
                            id={panelId}
                            megaMenu={item.megaMenu}
                            onLinkClick={closeMenu}
                          />
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </Column>

            <Column className="col-span-2 flex items-center justify-end">
              {props.actions ? (
                <NavigationActions
                  actions={props.actions}
                  onClose={closeActionPanel}
                  onToggle={toggleActionPanel}
                  openActionId={openActionId}
                  placement="desktop"
                />
              ) : null}
            </Column>
          </Row>
        </div>
      </Container>

      <MobileNavigation
        id={props.mobileMenuId}
        items={props.items}
        microcopies={props.microcopies}
        onClose={closeMobileMenu}
        open={isMobileMenuOpen}
        serviceLinks={props.serviceLinks}
        teaser={props.teaser}
      />
    </header>
  );
}

function getPanelId(idPrefix: string, itemId: string): string {
  return `${idPrefix}-mega-menu-${itemId}`;
}

function findItemElement(
  container: HTMLElement,
  selector: string,
  itemId: string
): HTMLElement | undefined {
  return Array.from(container.querySelectorAll<HTMLElement>(selector)).find(
    (element) => element.getAttribute(ITEM_ATTRIBUTE) === itemId
  );
}

function moveFocus(
  elements: HTMLElement[],
  current: HTMLElement,
  key: string,
  wrap = false
) {
  const lastIndex = elements.length - 1;
  const currentIndex = elements.indexOf(current);
  let nextIndex = currentIndex;

  if (key === "Home") {
    nextIndex = 0;
  } else if (key === "End") {
    nextIndex = lastIndex;
  } else if (key === "ArrowRight" || key === "ArrowDown") {
    nextIndex = currentIndex + 1;
  } else if (key === "ArrowLeft" || key === "ArrowUp") {
    nextIndex = currentIndex - 1;
  }

  if (wrap) {
    nextIndex = (nextIndex + elements.length) % elements.length;
  }

  elements[Math.min(Math.max(nextIndex, 0), lastIndex)]?.focus();
}
